import { z } from "zod";

/**
 * Profiles shared contract (Strangler-Fig migration, manifest-6, slice a14 /
 * N-3, Workstream C).
 *
 * Single source of truth for the `/profiles/*` wire shapes shared by the web
 * SPA (`src/repositories/profiles.ts`) and the NestJS slice
 * (`apps/api/src/companies/profiles.*`). The web repo `.parse()`s Nest
 * responses at the flip boundary so any FE<->BE drift surfaces at runtime (and
 * the repo falls back to the Supabase path).
 *
 * BE adoption note: `apps/api` does NOT import `@dallali/shared` at runtime —
 * the BE keeps field-for-field mirrors in
 * `apps/api/src/companies/dto/profiles.dto.ts`; THESE schemas are the contract
 * both sides mirror.
 */

/**
 * Response of `GET /profiles/me` (and `PATCH /profiles/me`) — the full
 * Supabase `profiles` Row in snake_case, so the FE can keep typing results as
 * `Database["public"]["Tables"]["profiles"]["Row"]` across the flip.
 */
export const profileMeResponseSchema = z.object({
  id: z.string().uuid(),
  company_id: z.string().uuid().nullable(),
  full_name: z.string().nullable(),
  full_name_en: z.string().nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  avatar_url: z.string().nullable(),
  national_id: z.string().nullable(),
  address: z.string().nullable(),
  is_active: z.boolean().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  nafath_verified: z.boolean().nullable(),
  nafath_verified_at: z.string().nullable(),
  fal_license_number: z.string().nullable(),
  fal_license_image_url: z.string().nullable(),
  has_password: z.boolean(),
  preferred_language: z.string(),
});
export type ProfileMeResponse = z.infer<typeof profileMeResponseSchema>;

/** Response of `GET /profiles/email-taken` — a boolean ONLY, never row data. */
export const emailTakenResponseSchema = z.object({
  taken: z.boolean(),
});
export type EmailTakenResponse = z.infer<typeof emailTakenResponseSchema>;

/**
 * Response of `GET /profiles/super-admin/lookup` — enveloped so a miss is an
 * explicit `{ profile: null }` (a bare null body would serialize as an empty
 * response). The row is the GrantSuperAdminDialog projection.
 */
export const superAdminProfileLookupRowSchema = z.object({
  id: z.string().uuid(),
  full_name: z.string().nullable(),
  full_name_en: z.string().nullable(),
  phone: z.string().nullable(),
  nafath_verified: z.boolean().nullable(),
});
export const superAdminProfileLookupResponseSchema = z.object({
  profile: superAdminProfileLookupRowSchema.nullable(),
});
export type SuperAdminProfileLookupResponse = z.infer<
  typeof superAdminProfileLookupResponseSchema
>;
